import { gql } from '@apollo/client';
import React, { useEffect, useRef, useState } from 'react';
import { MenuItem } from '../../../components/Menu';
import downloadIcon from '../../../icons/download.svg';
import download from '../../../utils/network/download';
import type { AttachmentValue } from '../../formats/Attachment';
import Attachment, {
  isAttachmentUploaded,
  shouldShowUploadingAttachment,
} from '../../formats/Attachment';
import type { HoveredBlotListenerSetting } from '../../HoveredBlotListener';
import { useHoveredBlotListener } from '../../HoveredBlotListener';
import { useQuill } from '../../QuillContext';
import ToolbarButton, { ToolbarButtonGroup } from './buttons/ToolbarButton';
import ToolbarCommentButtonGroup from './buttons/ToolbarCommentButtonGroup';
import ToolbarEditButton from './buttons/ToolbarEditButton';
import ToolbarOverflowMenuButton from './buttons/ToolbarOverflowMenuButton';
import FloatingToolbar from './FloatingToolbar';
import AddAttachmentMenuItem from './AddAttachmentMenuItem';
import useBlotHighlightedClass from './useBlotHighlightedClass';
import type {
  AttachmentToolbar_PostFragment,
  AttachmentToolbar_PublicPostFragment,
} from '../../../graphql/generated/graphqlTypes';
import type { Blot } from '../../types';

const downloadAll = ({
  attachments,
  post,
}: {
  attachments: AttachmentValue[];
  post: AttachmentToolbar_PostFragment | AttachmentToolbar_PublicPostFragment;
}) => {
  const query = new URLSearchParams(
    attachments.reduce((memo, { name, source }) => {
      const id = source.split('/').pop()?.replace(/\..+$/, '');
      return id ? { ...memo, [`att_${id}`]: name } : memo;
    }, {}),
  ).toString();

  const fileName = `Slab Attachments - ${post.title}.zip`;
  return download(`/posts/${post.id}/download?${query}`, fileName);
};

interface BaseAttachmentToolbarProps {
  hoveredBlot?: Blot;
  formats: Record<string, string[] | null | undefined>;
  publicPost?: boolean;
  readOnly?: boolean;
  handleDownloadAll: (() => Promise<void>) | null;
  appendAttachment?: (files: File[]) => void;
  setPersist?: (persist: boolean) => void;
}

export const BaseAttachmentToolbar = ({
  hoveredBlot,
  formats,
  publicPost,
  readOnly,
  handleDownloadAll,
  appendAttachment,
  setPersist,
}: BaseAttachmentToolbarProps) => {
  const unmounted = useRef(false);
  const [isPreparing, setIsPreparing] = useState(false);
  useBlotHighlightedClass(hoveredBlot);

  useEffect(() => {
    return () => {
      unmounted.current = true;
    };
  }, []);

  if (publicPost) {
    return (
      <ToolbarButtonGroup>
        <ToolbarButton
          tooltip={isPreparing ? 'Preparing...' : 'Download all (zip)'}
          disabled={isPreparing}
          icon={downloadIcon}
          onClick={() => {
            setIsPreparing(true);
            handleDownloadAll?.()
              .then(() => !unmounted.current && setIsPreparing(false))
              .catch(() => !unmounted.current && setIsPreparing(false));
          }}
        />
      </ToolbarButtonGroup>
    );
  }
  return (
    <>
      {readOnly && (
        <ToolbarButtonGroup>
          <ToolbarEditButton targetBlot={hoveredBlot} />
        </ToolbarButtonGroup>
      )}
      <ToolbarCommentButtonGroup
        blot={hoveredBlot}
        existingComment={formats.comment}
        onReactionPickerOpenChange={setPersist}
      />
      {(handleDownloadAll || !readOnly) && (
        <ToolbarButtonGroup>
          <ToolbarOverflowMenuButton onMenuOpenChange={setPersist}>
            {(onClose) => (
              <>
                {!readOnly && (
                  <AddAttachmentMenuItem
                    onUpload={(e) => {
                      appendAttachment?.(
                        Array.from(e.currentTarget.files ?? []),
                      );
                      onClose();
                    }}
                  />
                )}
                {handleDownloadAll && (
                  <MenuItem
                    icon={downloadIcon}
                    onClick={() => {
                      handleDownloadAll();
                      onClose();
                    }}
                  >
                    Download all (zip)
                  </MenuItem>
                )}
              </>
            )}
          </ToolbarOverflowMenuButton>
        </ToolbarButtonGroup>
      )}
    </>
  );
};

type Context = {
  formats: Record<string, string[] | null | undefined>;
  attachments: AttachmentValue[];
  appendAttachment: (files: File[]) => void;
};

const hoveredBlotListenerSetting: HoveredBlotListenerSetting<Context> = {
  getHoveredBlot(blot: Blot) {
    return blot.statics.blotName === Attachment.blotName ? blot : null;
  },
  getContext(blot: Attachment) {
    return {
      formats: blot.formats(),
      attachments: Attachment.value(blot.domNode as Element),
      appendAttachment: blot.appendAttachment,
    };
  },
};

const POST_FRAGMENT = gql`
  fragment AttachmentToolbar_post on Post {
    id
    title
  }
`;

const PUBLIC_POST_FRAGMENT = gql`
  fragment AttachmentToolbar_publicPost on PublicPost {
    id
    title
  }
`;

interface AttachmentToolbarProps {
  publicPost?: boolean;
  post: AttachmentToolbar_PostFragment | AttachmentToolbar_PublicPostFragment;
}

const AttachmentToolbar = ({ publicPost, post }: AttachmentToolbarProps) => {
  const { quill, readOnly } = useQuill();

  const {
    bounds,
    hoveredBlot,
    context: { formats, attachments, appendAttachment } = {
      formats: undefined,
      attachments: undefined,
      appendAttachment: undefined,
    },
    setPersist,
  } = useHoveredBlotListener(quill, hoveredBlotListenerSetting);

  const visibleAttachments = (attachments || []).filter(
    (attachment) =>
      isAttachmentUploaded(attachment) ||
      shouldShowUploadingAttachment(attachment),
  );

  const downloadableAttachments =
    visibleAttachments.filter(isAttachmentUploaded);

  const isHidden =
    // Attachment upload was failed, or other collaborator is uploading.
    !visibleAttachments.length ||
    (publicPost && !downloadableAttachments.length);

  const handleDownloadAll =
    downloadableAttachments.length > 0
      ? () =>
          downloadAll({
            post,
            attachments: downloadableAttachments,
          })
      : null;

  return (
    <FloatingToolbar bounds={isHidden ? null : bounds}>
      {hoveredBlot && formats && (
        <BaseAttachmentToolbar
          hoveredBlot={hoveredBlot}
          formats={formats}
          publicPost={publicPost}
          readOnly={readOnly}
          handleDownloadAll={handleDownloadAll}
          appendAttachment={appendAttachment}
          setPersist={setPersist}
        />
      )}
    </FloatingToolbar>
  );
};

AttachmentToolbar.fragments = {
  post: POST_FRAGMENT,
  publicPost: PUBLIC_POST_FRAGMENT,
};

export default AttachmentToolbar;
